"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { CircleCheck, Calendar, User, Mail, Phone, MessageSquare, ArrowRight, Sparkles } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
const benefits = ["Tecnologia avançada de diagnóstico", "Atendimento humanizado e personalizado", "Protocolos baseados em evidências científicas", "Foco no bem-estar feminino", "Resultados visíveis e duradouros", "Ambiente confortável e moderno"];
export default function FaleComEspecialista() {
  return <section id="fale-com-especialista" className="bg-background py-20 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-5%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[40%] h-[40%] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          
          {}
          <div className="space-y-8 lg:sticky lg:top-24">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                <Sparkles className="size-4" />
                <span>Comece sua transformação</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-foreground leading-tight">
                Por que agendar sua avaliação hoje?
              </h2>
              <p className="text-lg text-muted-foreground max-w-md">
                A saúde do seu cabelo reflete seu bem-estar. Não deixe para depois o cuidado que você merece agora.
              </p>
            </div>

            <div className="space-y-4">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex items-start gap-3 group">
                  <div className="mt-1 p-1 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <CircleCheck className="size-4" />
                  </div>
                  <span className="text-foreground/80 font-medium">{benefit}</span>
                </div>)}
            </div>

            <div className="p-6 bg-card rounded-2xl border border-border shadow-sm">
              <div className="flex items-center gap-4">
                <div className="flex -space-x-3">
                  <div className="size-10 rounded-full bg-gray-200 border-2 border-background overflow-hidden">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/avatar_female_1.png" alt="Client" className="w-full h-full object-cover" />
                  </div>
                  <div className="size-10 rounded-full bg-gray-200 border-2 border-background overflow-hidden">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/avatar_female_2.png" alt="Client" className="w-full h-full object-cover" />
                  </div>
                  <div className="size-10 rounded-full bg-gray-200 border-2 border-background overflow-hidden">
                    <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1559261/img/avatar_female_3.png" alt="Client" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">Junte-se a centenas de mulheres</p>
                  <p className="text-xs text-muted-foreground">que recuperaram sua autoestima.</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="bg-card rounded-3xl shadow-xl border border-border/50 p-6 md:p-8 lg:p-10">
            <div className="mb-8">
              <h3 className="text-2xl font-serif text-foreground mb-2">Fale com Especialista</h3>
              <p className="text-muted-foreground text-sm">
                Preencha o formulário abaixo para solicitar seu agendamento. Nossa equipe entrará em contato para confirmar a disponibilidade.
              </p>
            </div>

            <FormProvider formId="contact_specialist_form_scheduling" sectionName="fale-com-especialista" defaultValues={{
            nome: "",
            email: "",
            telefone: "",
            tipo_problema: "",
            data_preferida: "",
            mensagem: ""
          }} validationRules={{
            nome: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            telefone: {
              required: true,
              minLength: 10
            },
            tipo_problema: {
              required: true
            },
            mensagem: {
              required: true,
              minLength: 10
            }
          }} submitText="Solicitar Agendamento" successMessage="Sua solicitação foi enviada com sucesso! Em breve entraremos em contato." errorMessage="Ocorreu um erro ao enviar. Por favor, tente novamente.">
              <SpecialistForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function SpecialistForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isSubmitting) {
      setProgressValue(0);
      interval = setInterval(() => {
        setProgressValue(prev => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 200);
    } else if (isSubmitted && !submitError) {
      setProgressValue(100);
    } else {
      setProgressValue(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting, isSubmitted, submitError]);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-2">
          <CircleCheck className="size-8" />
        </div>
        <h4 className="text-xl font-semibold text-foreground">Solicitação Recebida!</h4>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Enviar nova mensagem
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField control={control} name="nome" rules={{
          required: "Nome é obrigatório"
        }} render={({
          field
        }) => <FormItem className="col-span-1 md:col-span-2">
                <FormLabel>Nome Completo</FormLabel>
                <FormControl>
                  <div className="relative">
                    <User className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                    <Input placeholder="Maria Silva" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "E-mail é obrigatório"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>E-mail</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                    <Input type="email" placeholder="maria@email.com" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="telefone" rules={{
          required: "Telefone é obrigatório"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel>Telefone</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 size-4 text-muted-foreground" />
                    <Input type="tel" placeholder="(11) 91234-5678" className="pl-9" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        {}
        <div className="pt-4 border-t border-border/50">
          <h4 className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">Detalhes do Atendimento</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField control={control} name="tipo_problema" rules={{
            required: "Selecione um tipo de problema"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Tipo de Problema Capilar</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o problema" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {["Oleosidade", "Caspa", "Psoríase", "Queda de Cabelo", "Alopecia", "Danos Químicos", "Manutenção Preventiva"].map(option => <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>)}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="data_preferida" render={({
            field
          }) => <FormItem>
                  <FormLabel>Data Preferida (Opcional)</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <SafeHydrate><Calendar className="absolute left-3 top-2.5 size-4 text-muted-foreground" /></SafeHydrate>
                      <Input type="date" className="pl-9" {...field} />
                    </div>
                  </FormControl>
                  <FormDescription className="text-xs">
                    Sujeito a confirmação.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <FormField control={control} name="mensagem" rules={{
        required: "Mensagem é obrigatória"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Mensagem</FormLabel>
              <FormControl>
                <div className="relative">
                  <MessageSquare className="absolute left-3 top-3 size-4 text-muted-foreground" />
                  {}
                  <textarea className="flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 pl-9 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50" placeholder="Descreva seu caso ou dúvidas..." {...field} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="space-y-3">
        {isSubmitting && <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Enviando solicitação...</span>
              <span>{progressValue}%</span>
            </div>
            <Progress value={progressValue} className="h-1.5" />
          </div>}
        
        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium">
          {isSubmitting ? "Processando..." : "Solicitar Agendamento"}
          {!isSubmitting && <ArrowRight className="ml-2 size-4" />}
        </Button>
        
        <p className="text-center text-xs text-muted-foreground">
          Ao enviar, você concorda com nossa política de privacidade.
        </p>
      </div>
    </Form>;
}